import { useEffect } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';

/**
 * Initializes PostHog and wraps children with the PostHogProvider context.
 * - Tracking is DISABLED in development (NODE_ENV !== 'production') to save event quota.
 * - Session Recording and Autocapture are enabled in production.
 */

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Initialize PostHog only once, and only in production
if (IS_PRODUCTION && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Session Recording
    session_recording: {
      maskAllInputs: true,       // PII safety: mask form inputs in recordings
      maskTextSelector: '[data-ph-mask]', // mask elements with this attribute
    },
    // Autocapture for clicks, form submits, page views
    autocapture: true,
    // Capture page views automatically on route changes
    capture_pageview: true,
    capture_pageleave: true,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Persistence
    persistence: 'localStorage+cookie',
    // Loaded callback
    loaded: (ph) => {
      // Optionally enable debug mode via URL param: ?posthog_debug=true
      if (window.location.search.includes('posthog_debug=true')) {
        ph.debug();
      }
    },
  });
}

/**
 * PostHogProvider component — wraps your app tree.
 * In development, it renders children without the provider to avoid sending events.
 */
const PostHogProvider = ({ children }) => {
  if (!IS_PRODUCTION || !POSTHOG_KEY) {
    // In dev mode, just render children without PostHog context
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      {children}
    </PHProvider>
  );
};

/**
 * Safe wrapper around usePostHog that returns a no-op capture in development.
 * Usage:
 *   const posthog = useSafePostHog();
 *   posthog.capture('event_name', { key: 'value' });
 */
const useSafePostHog = () => {
  if (!IS_PRODUCTION || !POSTHOG_KEY) {
    // Return a mock object so calling code never crashes in dev
    return {
      capture: (event, props) => {
        console.log(`[PostHog DEV] ${event}`, props || '');
      },
      identify: () => {},
      reset: () => {},
      people: { set: () => {} },
    };
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return usePostHog();
};

export default PostHogProvider;
export { useSafePostHog, posthog };
